import { c as _c } from "react/compiler-runtime";
import { type InputHTMLAttributes, type ReactNode, type Ref, useState } from 'react';
import { cva } from 'class-variance-authority';
import { Eye, EyeOff } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { FormMessage } from './form';
import { Icons } from './icons';
import { cn } from './lib/cn';
import { Text } from './text';
const inputVariants = cva(`flex h-12 px-3 transition-all w-full text-[14px] text-role-on-background rounded-2xl bg-role-background
placeholder-role-outline-subtle
border border-role-outline-subtle
not-placeholder-shown:border-role-info-outline
hover:border-role-outline-subtle hover:bg-[#f5f5f5]
focus-visible:bg-role-background focus-visible:border-role-outline-subtle focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-role-info-outline
disabled:cursor-not-allowed disabled:border-role-disabled disabled:bg-role-disabled disabled:text-role-on-disabled
file:border-0 file:bg-transparent file:text-[14px] file:font-medium
shadow-none outline-hidden focus-visible:shadow-none
box-border`, {
  variants: {
    error: {
      true: 'border-role-error-outline not-placeholder-shown:border-role-error-outline hover:border-role-error-outline focus-visible:border-role-error-outline focus-visible:ring-role-error-outline',
      false: ''
    },
    icon: {
      true: 'pl-10',
      false: ''
    }
  },
  defaultVariants: {}
});
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  icon?: ReactNode;
  showTip?: boolean;
}
const Input = t0 => {
  const $ = _c(29);
  let className;
  let icon;
  let props;
  let ref;
  let t1;
  let type;
  if ($[0] !== t0) {
    ({
      className,
      type,
      icon,
      error: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = icon;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
    $[6] = type;
  } else {
    className = $[1];
    icon = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
    type = $[6];
  }
  const error = t1 === undefined ? false : t1;
  if (icon) {
    let t2;
    if ($[7] !== icon) {
      t2 = <div className="absolute inset-y-0 left-0 flex w-10 items-center justify-center text-black">{icon}</div>;
      $[7] = icon;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== className || $[10] !== error || $[11] !== icon) {
      t3 = cn(inputVariants({
        error,
        icon: Boolean(icon),
        className
      }));
      $[9] = className;
      $[10] = error;
      $[11] = icon;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    let t4;
    if ($[13] !== props || $[14] !== ref || $[15] !== t3 || $[16] !== type) {
      t4 = <input {...props} className={t3} ref={ref} type={type} />;
      $[13] = props;
      $[14] = ref;
      $[15] = t3;
      $[16] = type;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t5;
    if ($[18] !== t2 || $[19] !== t4) {
      t5 = <div className="relative flex w-full">{t2}{t4}</div>;
      $[18] = t2;
      $[19] = t4;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    return t5;
  }
  let t2;
  if ($[21] !== className || $[22] !== error) {
    t2 = cn(inputVariants({
      error,
      className
    }));
    $[21] = className;
    $[22] = error;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== props || $[25] !== ref || $[26] !== t2 || $[27] !== type) {
    t3 = <input className={t2} ref={ref} type={type} {...props} />;
    $[24] = props;
    $[25] = ref;
    $[26] = t2;
    $[27] = type;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  return t3;
};
Input.displayName = 'Input';
function PasswordInputTip(t0) {
  const $ = _c(15);
  const {
    text,
    rule
  } = t0;
  const t1 = rule ? "border-green-medium bg-green-light text-green-dark" : "border-gray-medium text-black";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex h-4 w-4 items-center justify-center rounded-2xl border", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== rule) {
    t3 = rule ? <Icons.Check className="h-[6px] w-[8px]" /> : null;
    $[2] = rule;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = rule ? "text-black" : "text-gray-dark";
  let t6;
  if ($[7] !== t5) {
    t6 = cn(t5, "font-medium text-base");
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6 || $[10] !== text) {
    t7 = <Text className={t6}>{text}</Text>;
    $[9] = t6;
    $[10] = text;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t7) {
    t8 = <li className="flex items-center justify-center gap-2">{t4}{t7}</li>;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
const InputPassword = t0 => {
  const $ = _c(51);
  let className;
  let props;
  let ref;
  let t1;
  let t2;
  let type;
  if ($[0] !== t0) {
    ({
      className,
      type,
      error: t1,
      showTip: t2,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
    $[5] = t2;
    $[6] = type;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
    t2 = $[5];
    type = $[6];
  }
  const error = t1 === undefined ? false : t1;
  const showTip = t2 === undefined ? false : t2;
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  let t3;
  if ($[7] !== showPassword) {
    t3 = () => setShowPassword(!showPassword);
    $[7] = showPassword;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const togglePasswordVisibility = t3;
  const charsOrMore = String(props.value ?? "").length >= 8;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = /(?=.*?[A-Z])/g;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== props.value) {
    t5 = t4.test(String(props.value ?? ""));
    $[10] = props.value;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const atLeastOneUppercase = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = /(?=.*?[a-z])/g;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== props.value) {
    t7 = t6.test(String(props.value ?? ""));
    $[13] = props.value;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const atLeastOneLowercase = t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = /(?=.*?[0-9])/g;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== props.value) {
    t9 = t8.test(String(props.value ?? ""));
    $[16] = props.value;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const atLeastOneNumeric = t9;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = /(.*[!@#$%^&*(),.?":{}|<>].*)/g;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== props.value) {
    t11 = t10.test(String(props.value ?? ""));
    $[19] = props.value;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const atLeastOneSpecial = t11;
  let t12;
  if ($[21] !== className || $[22] !== error) {
    t12 = cn(inputVariants({
      error,
      className
    }));
    $[21] = className;
    $[22] = error;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = showPassword ? "text" : type;
  let t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setIsFocused(false);
    };
    t15 = () => {
      setIsFocused(true);
    };
    $[24] = t14;
    $[25] = t15;
  } else {
    t14 = $[24];
    t15 = $[25];
  }
  let t16;
  if ($[26] !== props || $[27] !== ref || $[28] !== t12 || $[29] !== t13) {
    t16 = <input className={t12} ref={ref} type={t13} {...props} onBlur={t14} onFocus={t15} />;
    $[26] = props;
    $[27] = ref;
    $[28] = t12;
    $[29] = t13;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== showPassword || $[32] !== togglePasswordVisibility) {
    t17 = <div className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-gray-400">{showPassword ? <EyeOff className="size-4" onClick={togglePasswordVisibility} /> : <Eye className="size-4" onClick={togglePasswordVisibility} />}</div>;
    $[31] = showPassword;
    $[32] = togglePasswordVisibility;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t16 || $[35] !== t17) {
    t18 = <div className="relative">{t16}{t17}</div>;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <FormMessage />;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== atLeastOneLowercase || $[39] !== atLeastOneNumeric || $[40] !== atLeastOneSpecial || $[41] !== atLeastOneUppercase || $[42] !== charsOrMore || $[43] !== isFocused || $[44] !== showTip) {
    t20 = showTip && isFocused && <motion.div animate={{
      height: "auto"
    }} exit={{
      height: 0
    }} initial={{
      height: 0
    }} key="password-input-tip-container" style={{
      overflow: "hidden"
    }} transition={{
      ease: "easeInOut",
      delay: 0.2,
      duration: 0.8,
      type: "spring",
      stiffness: 300,
      damping: 30
    }}><ul className="flex flex-col items-start justify-center gap-2 rounded-2xl border border-gray-light p-4"><li className="mb-2"><Text body="xs" className="font-medium text-base text-black leading-5">Password must contain at least:</Text></li><PasswordInputTip rule={charsOrMore} text="8 characters" /><PasswordInputTip rule={atLeastOneUppercase} text="1 Upper case character" /><PasswordInputTip rule={atLeastOneLowercase} text="1 Lower case character" /><PasswordInputTip rule={atLeastOneNumeric} text="1 Number" /><PasswordInputTip rule={atLeastOneSpecial} text="1 Special Character (e.g !, @, #, ...)" /></ul></motion.div>;
    $[38] = atLeastOneLowercase;
    $[39] = atLeastOneNumeric;
    $[40] = atLeastOneSpecial;
    $[41] = atLeastOneUppercase;
    $[42] = charsOrMore;
    $[43] = isFocused;
    $[44] = showTip;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t20) {
    t21 = <AnimatePresence mode="wait">{t20}</AnimatePresence>;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t18 || $[49] !== t21) {
    t22 = <>{t18}{t19}{t21}</>;
    $[48] = t18;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  return t22;
};
InputPassword.displayName = 'InputPassword';
export { Input, InputPassword };